import { useEffect } from "react";
import { useWindowEvent } from "@mantine/hooks";

// Every open Mantine modal and drawer closes on Escape through its own window keydown listener, in the capture phase.
// A listener skips the key only when the event's target carries this attribute, which is how a select's dropdown
// keeps Escape for itself. It is the only hook the others offer, so the dialog uses it.
const STOP_ATTRIBUTE = "data-mantine-stop-propagation";

/**
 * Makes Escape belong to this dialog alone while it is open: `onEscape` runs, and the modals and drawers behind it
 * don't close too. That matters most for an inspection, where closing the modal behind would lose the form and photos.
 *
 * The others look at the event's target, and that is whatever has the focus. So while the dialog is open, the focused
 * element carries the attribute, wherever the focus is, and so does the page body for when nothing has it. They go
 * back to normal when the dialog closes. The dialog's own Modal must have `closeOnEscape` off, or it skips Escape
 * for the same reason.
 */
export const useEscapeForThisDialog = (opened: boolean, onEscape: () => void) => {
  useEffect(() => {
    if (!opened) return;

    const marked: Element[] = [];
    const mark = (element: Element | null) => {
      // An element that already has it (set by Mantine itself) stays as it is when the dialog closes.
      if (!element || element.hasAttribute(STOP_ATTRIBUTE)) return;
      element.setAttribute(STOP_ATTRIBUTE, "true");
      marked.push(element);
    };
    const markFocused = (event: FocusEvent) => mark(event.target instanceof Element ? event.target : null);

    mark(document.activeElement);
    mark(document.body);
    document.addEventListener("focusin", markFocused);
    return () => {
      document.removeEventListener("focusin", markFocused);
      marked.forEach((element) => element.removeAttribute(STOP_ATTRIBUTE));
    };
  }, [opened]);

  useWindowEvent("keydown", (event) => {
    if (opened && event.key === "Escape" && !event.isComposing) onEscape();
  });
};
